/**
 * Operations › PC switches (owner, 2026-10-21: "why do we store stuff locally that has to be done on the pc with intune?
 * why dont we have that as server side switches?"; the screen approved as drawn in CommandCenter, 2026-10-21 17:33 PT,
 * with the PCs table showing "computer name and UPN of user"): the shapes GET/PUT /v1/admin/desktop/policy and GET
 * /v1/admin/desktop/policy/devices answer (services/gyro-svc/src/desktopPolicy.ts, docs/DESIGN-pc-switches-2026-10-21.md),
 * the words the screen shows, and the arithmetic over unsaved edits, exceptions and the PCs' reports.
 *
 * The words come from the service's catalog, which speaks the approved screen's words (label, shortLabel, inverted,
 * description, and the groups in screen order). A catalog from before that (no `shortLabel`), or none at all, falls back
 * to FALLBACK_CATALOG, a copy of the service's that test/pc-switches.test.ts holds equal to it. The few words the catalog
 * does not carry (each choice's words, the panels' lines) are the mockup's, below. One switch reads the other way round:
 * "Gyro runs on the PC" is On when the stored deviceDisabled is false (`inverted`).
 *
 * No React, no network.
 */

/** A switch's value: on/off, one of its choices, or a list of names. */
export type SwitchValue = boolean | string | string[];
/** One level of the policy (everyone, or one person's exceptions): a key that is absent is not set there. */
export type Switches = Record<string, SwitchValue>;

/** One switch as the service describes it (desktopPolicyCatalog.ts CatalogEntry). */
export interface CatalogEntry {
  key: string;
  /** The switch's name on the screen; for an on/off switch, what ON means there (see `inverted`). */
  label: string;
  /** Its name on a chip ("Local connections: Off"). Absent in a catalog from before the screen's words. */
  shortLabel?: string;
  group: string;
  type: 'boolean' | 'choice' | 'list';
  choices?: readonly string[];
  /** The screen shows the opposite of the stored value (deviceDisabled: "Gyro runs on the PC"). */
  inverted?: boolean;
  /** One line saying what the switch does. */
  description: string;
  /** 'updater': read by the PC's SYSTEM updater, so it applies once the new updater is installed. */
  appliesVia: 'agent' | 'updater';
  limits?: { entries: number; chars: number };
}

/** A person's exceptions; `upn` is the sign-in e-mail their PC's enrollment recorded, `name` is not known to the service today. */
export interface PolicyPerson {
  switches: Switches;
  name?: string;
  upn?: string;
}

/** GET and PUT /v1/admin/desktop/policy. */
export interface DesktopPolicyView {
  revision: number;
  updatedAt: string | null;
  /** The e-mail of the admin who saved last. */
  updatedBy: string | null;
  fleet: Switches;
  /** Exceptions by Entra object id. */
  people: Record<string, PolicyPerson>;
  catalog: CatalogEntry[];
}

/** PUT /v1/admin/desktop/policy: the whole policy, and the revision it was loaded at. */
export interface DesktopPolicyBody {
  fleet: Switches;
  people: Record<string, { switches: Switches }>;
  expectedRevision: number;
}

/** Where a PC says a value came from (POST /v1/desktop/policy/report `sources`). */
export type SwitchSource = 'server' | 'machine' | 'default' | 'quarantine' | 'user-pause';

/** One row of GET /v1/admin/desktop/policy/devices: a device key's last report. */
export interface DevicePolicyRow {
  deviceId: string;
  /** The PC's Windows computer name: its report's, else the name it enrolled with; null when neither is known. */
  computerName: string | null;
  /** The key's owner; null for a key with no owner. */
  person: { oid: string; name?: string; upn?: string } | null;
  agentVersion: string;
  /** The policy revision the PC applied. */
  revision: number;
  reportedAt: string;
  receivedAt: string;
  effective: Switches;
  sources: Partial<Record<string, SwitchSource>>;
  /** The fleet keys whose value the PC does not hold (a person's exception shows here too). */
  differsFromFleet: string[];
  /** The update circle the service sets for the PC and why; absent from a service before the circles. */
  circle?: PcCircle;
}

// ---- Update circles (desktopUpdateCircles.ts; owner, 2026-10-27) ----

/** Where a PC's update circle comes from: the person's exception, an Early circle group, For everyone, or nothing set. */
export type CircleSource = 'exception' | 'group' | 'fleet' | 'none';

export interface PcCircle {
  /** The ring the service sets for the PC (`broad` is Everyone); null when it sets none. */
  ring: string | null;
  source: CircleSource;
  groupId?: string;
}

export type CircleGroupState = 'waiting-for-bridge' | 'not-read-yet' | 'refused' | 'in-effect';

/** One Early circle group as GET /v1/admin/desktop/policy/circles describes it. */
export interface CircleGroupStatus {
  groupId: string;
  state: CircleGroupState;
  inEffect: boolean;
  members?: number;
  readAtMs?: number;
  reason?: string;
  detail?: string;
}

/** GET and PUT /v1/admin/desktop/policy/circles. */
export interface CirclesView {
  revision: number;
  earlyGroups: CircleGroupStatus[];
  limits: { earlyGroups: number };
}

/** PUT /v1/admin/desktop/policy/circles: every Early circle group, and the revision they were loaded at. */
export interface CirclesBody {
  earlyGroups: string[];
  expectedRevision: number;
}

export const CIRCLES_HINT = 'Members of these Entra groups get Early releases before everyone else, unless they have their own Update circle exception. Add a group by its object id; add one person through Exceptions for people.';
export const CIRCLES_SAVED_TEXT = 'Saved. Each PC follows its circle at its next update check, within the hour.';
export const CIRCLES_CHANGED_TEXT = 'Another admin changed the Early circle groups after you loaded them, so nothing was saved. They are reloaded below: check them and make your change again.';
export const CIRCLES_UNREADABLE_TEXT = 'Gyro could not read the Early circle groups just now, so they cannot be changed here. Reload in a minute.';

const GROUP_OBJECT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** A group id as typed, normalised (trimmed, lower case). */
export function circleGroupIdOf(value: string): string {
  return value.trim().toLowerCase();
}

/** Why a typed group id cannot be added, or null when it can. */
export function circleGroupAddRefusal(value: string, listed: readonly string[], limit: number): string | null {
  const id = circleGroupIdOf(value);
  if (!GROUP_OBJECT_ID_RE.test(id)) return "Enter the group's object id: a GUID such as 00000000-0000-0000-0000-000000000000.";
  if (listed.includes(id)) return 'That group is already listed.';
  if (listed.length >= limit) return `At most ${limit} groups can be listed.`;
  return null;
}

/** Whether the groups as edited differ from what the service holds. */
export function circlesChanged(groups: readonly string[], stored: Pick<CirclesView, 'earlyGroups'>): boolean {
  const kept = stored.earlyGroups.map((group) => group.groupId);
  return groups.length !== kept.length || groups.some((groupId, index) => groupId !== kept[index]);
}

export function circlesSaveBody(groups: readonly string[], expectedRevision: number): CirclesBody {
  return { earlyGroups: [...groups], expectedRevision };
}

/** A group's status line: "In effect: 12 members, read from The Bridge at 09:15." */
export function circleGroupText(status: CircleGroupStatus, options: { timeZone?: string } = {}): string {
  const at = (ms: number | undefined) => (ms === undefined ? '' : ` at ${new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...(options.timeZone ? { timeZone: options.timeZone } : {}) })}`);
  const count = (n: number | undefined) => `${n ?? 0} member${n === 1 ? '' : 's'}`;
  switch (status.state) {
    case 'waiting-for-bridge':
      return 'Not in effect yet. The group takes effect once The Bridge connection for gyro-svc is live; until then its members follow For everyone.';
    case 'not-read-yet':
      return 'Not in effect yet: Gyro has not had the group’s members from The Bridge yet. Reload in a minute.';
    case 'refused':
      return status.inEffect
        ? `In effect with the ${count(status.members)} last read: The Bridge refused the latest read${at(status.readAtMs)} (${status.reason ?? 'refused'}). Gyro asks again every minute.`
        : `Not in effect: The Bridge refused the read${at(status.readAtMs)} (${status.reason ?? 'refused'}) and the group has never been read, so its members follow For everyone. Gyro asks again every minute.`;
    case 'in-effect':
      return `In effect: ${count(status.members)}, read from The Bridge${at(status.readAtMs)}. Changes to the group show within about an hour.`;
  }
}

/** The PCs table's Circle cell: the circle and where it comes from ("Early · group", "Canary · exception", "IT setting"). */
export function circleText(row: Pick<DevicePolicyRow, 'circle'>, catalog: readonly CatalogEntry[]): { circle: string; why: string } | null {
  const circle = row.circle;
  if (!circle) return null;
  const entry = catalog.find((candidate) => candidate.key === 'ring');
  const name = circle.ring === null ? 'IT setting' : entry ? choiceWords(wordsFor(entry), circle.ring).chip : circle.ring;
  const why = circle.source === 'exception' ? 'their exception' : circle.source === 'group' ? 'Early circle group' : circle.source === 'fleet' ? 'for everyone' : 'nothing set here';
  return { circle: name, why };
}

// ---- The catalog ----

/**
 * The service's catalog (desktopPolicyCatalog.ts SWITCH_CATALOG, in the approved screen's words), for a service that sent
 * none or sent one from before those words: the same entries in the same order.
 */
export const FALLBACK_CATALOG: readonly CatalogEntry[] = Object.freeze<CatalogEntry[]>([
  {
    key: 'deviceDisabled', label: 'Gyro runs on the PC', shortLabel: 'Gyro on this PC', group: 'Gyro on this PC', type: 'boolean', inverted: true,
    description: 'Off stops Gyro on the PC: no assistant, no mascot and no PC actions until it is on again.', appliesVia: 'agent',
  },
  {
    key: 'pauseAutonomy', label: 'Pause autonomy', shortLabel: 'Pause autonomy', group: 'Autonomy', type: 'boolean',
    description: 'On: Gyro asks before every action on the PC, even ones it may normally do on its own.', appliesVia: 'agent',
  },
  {
    key: 'autonomyCeiling', label: 'Autonomy limit', shortLabel: 'Autonomy limit', group: 'Autonomy', type: 'choice',
    choices: ['standard', 'sensitive', 'financial', 'security', 'break-glass'],
    description: 'The riskiest kind of action Gyro may do on its own. Financial, security and break-glass actions always ask first.', appliesVia: 'agent',
  },
  {
    key: 'mcpDiscoveryEnabled', label: 'Look for local connections', shortLabel: 'Local connections', group: 'Local connections', type: 'boolean',
    description: 'Gyro checks the PC for tools already set up in Claude Desktop, Claude Code and Revu.', appliesVia: 'agent',
  },
  {
    key: 'mcpImportExistingEnabled', label: 'Use existing Claude and Revu connections', shortLabel: 'Claude and Revu connections', group: 'Local connections', type: 'boolean',
    description: 'Gyro uses the tools the person already set up in Claude, and the Revu connection, without a separate list.', appliesVia: 'agent',
  },
  {
    key: 'mcpImportRemoteEnabled', label: 'Allow remote connections', shortLabel: 'Remote connections', group: 'Local connections', type: 'boolean',
    description: 'Also use Claude connections that reach an online service. They carry their own sign-in, so they stay off unless allowed.', appliesVia: 'agent',
  },
  {
    key: 'mcpAllowList', label: 'Allow list', shortLabel: 'Allow list', group: 'Local connections', type: 'list',
    description: 'Connections Gyro may use by their exact name, even with Use existing or Allow remote off. Needs Look for local connections on.', appliesVia: 'agent',
    limits: { entries: 200, chars: 200 },
  },
  {
    key: 'gyroAiForRevitProvisioningEnabled', label: 'Set up Gyro AI for Revit', shortLabel: 'Gyro AI for Revit setup', group: 'Gyro AI for Revit', type: 'boolean',
    description: 'Installs Gyro AI for Revit for each person on the PC and connects it to Gyro and Claude. Needs Look for local connections and Use existing connections on.',
    appliesVia: 'agent',
  },
  {
    key: 'gyroAiForRevitPyRevitPanel', label: 'Its pyRevit panel', shortLabel: 'Gyro AI for Revit panel', group: 'Gyro AI for Revit', type: 'boolean',
    description: 'Shows the Gyro AI for Revit panel inside Revit. Off leaves the panel to the Orbit Revit add-in.', appliesVia: 'agent',
  },
  {
    key: 'claudeSkillsEnabled', label: 'Claude skills', shortLabel: 'Claude skills', group: 'Claude', type: 'boolean',
    description: 'Gyro uses the skills the person saved in Claude when it answers.', appliesVia: 'agent',
  },
  {
    key: 'claudeHandoffEnabled', label: 'Hand-off to Claude', shortLabel: 'Hand-off to Claude', group: 'Claude', type: 'boolean',
    description: 'An Open in Claude button sends a draft to Claude Desktop on the PC, after the person checks it.', appliesVia: 'agent',
  },
  {
    key: 'localFilesEnabled', label: 'Local files', shortLabel: 'Local files', group: 'Files and screen', type: 'boolean',
    description: 'Gyro can search, read and create files in folders the person connects. The person checks every change first.', appliesVia: 'agent',
  },
  {
    key: 'uiaReadsEnabled', label: 'Reading the screen', shortLabel: 'Reading the screen', group: 'Files and screen', type: 'boolean',
    description: 'Gyro can read the text of a window the person names. Private details are hidden on the PC before anything leaves it.', appliesVia: 'agent',
  },
  {
    key: 'elevationEnabled', label: 'Admin tasks (elevation)', shortLabel: 'Admin tasks', group: 'Admin tasks', type: 'boolean',
    description: 'Gyro may use a time-limited admin role on the PC for IT tasks that were approved.', appliesVia: 'agent',
  },
  {
    key: 'otaEnabled', label: 'Updates', shortLabel: 'Updates', group: 'Updates', type: 'boolean',
    description: 'Gyro installs new versions on the PC by itself.', appliesVia: 'updater',
  },
  {
    key: 'ring', label: 'Update circle', shortLabel: 'Update circle', group: 'Updates', type: 'choice', choices: ['canary', 'early', 'broad'],
    description: 'Which releases the PC gets: Canary first, then Early, then Everyone. People in an Early circle group get Early.', appliesVia: 'updater',
  },
]);

const FALLBACK_BY_KEY: ReadonlyMap<string, CatalogEntry> = new Map(FALLBACK_CATALOG.map((entry) => [entry.key, entry]));

/** The catalog the screen draws: the service's, or the contract's own when the service sent none. */
export function catalogOf(view: Pick<DesktopPolicyView, 'catalog'> | null | undefined): readonly CatalogEntry[] {
  const served = Array.isArray(view?.catalog) ? view.catalog.filter((entry) => entry && typeof entry.key === 'string') : [];
  return served.length > 0 ? served : FALLBACK_CATALOG;
}

/** Whether a catalog entry carries the approved screen's words (the service's catalog has had `shortLabel` since then). */
export function speaksTheScreen(entry: CatalogEntry): boolean {
  return typeof entry.shortLabel === 'string' && entry.shortLabel.trim() !== '';
}

/** The entry whose words the screen uses: the service's own when it speaks the screen's words, else the fallback copy. */
function wordsSource(entry: CatalogEntry): CatalogEntry {
  return speaksTheScreen(entry) ? entry : FALLBACK_BY_KEY.get(entry.key) ?? entry;
}

// ---- The words ----

/** How one choice reads: in the drop-down or control, and in a chip ("Autonomy limit: Up to sensitive"). */
export interface ChoiceWords {
  option: string;
  chip: string;
}

/** The words one switch is shown with. */
export interface SwitchWords {
  /** The row's name. */
  label: string;
  /** One line saying what it does. */
  description: string;
  /** The name in a chip ("Local connections: Off"). */
  short: string;
  /** The words say what the stored key's OFF means: deviceDisabled is "Gyro runs on the PC", On when it is false. */
  inverted?: boolean;
  /** How the line under a row names people with an exception: by value ("Off for 1 person") or "Different for 1 person". */
  summary?: 'value' | 'different';
  /** The choices offered for everyone and in an exception, as drawn; a stored value outside them is still shown. */
  offered?: readonly string[];
  choices?: Record<string, ChoiceWords>;
}

/** The mockup's words for what the catalog does not word: each choice, which choices are offered, the line under a row. */
const CHOICE_WORDS: Readonly<Record<string, Pick<SwitchWords, 'summary' | 'offered' | 'choices'>>> = Object.freeze({
  autonomyCeiling: {
    summary: 'different',
    offered: ['standard', 'sensitive'],
    choices: {
      standard: { option: 'Standard actions only', chip: 'Standard only' },
      sensitive: { option: 'Up to sensitive actions', chip: 'Up to sensitive' },
      financial: { option: 'Up to financial actions', chip: 'Up to financial' },
      security: { option: 'Up to security actions', chip: 'Up to security' },
      'break-glass': { option: 'Up to break-glass actions', chip: 'Up to break-glass' },
    },
  },
  mcpAllowList: { summary: 'different' },
  ring: {
    // The feed's `broad` ring is the Everyone circle (owner, 2026-10-27: an Early circle and an Everyone circle).
    choices: {
      canary: { option: 'Canary', chip: 'Canary' },
      early: { option: 'Early', chip: 'Early' },
      broad: { option: 'Everyone', chip: 'Everyone' },
    },
  },
});

/** The words a switch is shown with: the catalog's, the choices' from the mockup. */
export function wordsFor(entry: CatalogEntry): SwitchWords {
  const source = wordsSource(entry);
  return {
    label: source.label,
    description: source.description,
    short: source.shortLabel?.trim() || source.label,
    ...(source.inverted === true ? { inverted: true } : {}),
    ...(CHOICE_WORDS[entry.key] ?? {}),
  };
}

/** The panel (group) a switch is shown in. */
export function groupOf(entry: CatalogEntry): string {
  return wordsSource(entry).group;
}

/** One panel of For everyone, in the mockup's order: its group, icon id and the line under its title. */
export interface PanelWords {
  id: string;
  title: string;
  description: string;
}

export const PANELS: readonly PanelWords[] = Object.freeze([
  { id: 'gyro', title: 'Gyro on this PC', description: 'Turn Gyro off on a PC without uninstalling it' },
  { id: 'autonomy', title: 'Autonomy', description: 'How much Gyro may do on the PC without asking first' },
  { id: 'connections', title: 'Local connections', description: 'Tools already set up on the PC that Gyro may use, such as Claude and Revu' },
  { id: 'revit', title: 'Gyro AI for Revit', description: 'The Revit tools that come with Gyro' },
  { id: 'claude', title: 'Claude', description: 'Working with Claude on the PC' },
  { id: 'files', title: 'Files and screen', description: 'What Gyro may read and change on the PC' },
  { id: 'admin', title: 'Admin tasks', description: 'Temporary admin rights for IT tasks on the PC' },
  { id: 'updates', title: 'Updates', description: 'How Gyro keeps itself up to date on the PC' },
]);

/** A panel as drawn: its words and the catalog's switches in it, in catalog order. */
export interface PanelView extends PanelWords {
  entries: CatalogEntry[];
  /** Every switch in it is read by the PC's updater: "Applies once the new updater is installed". */
  viaUpdater: boolean;
}

/** The panels For everyone shows: the mockup's eight in its order, then any other group the catalog names, in catalog order. */
export function panelsFor(catalog: readonly CatalogEntry[]): PanelView[] {
  const panels: PanelView[] = PANELS.map((panel) => ({ ...panel, entries: [], viaUpdater: false }));
  for (const entry of catalog) {
    const group = groupOf(entry);
    let panel = panels.find((candidate) => candidate.title === group);
    if (!panel) {
      panel = { id: `group-${group}`, title: group, description: '', entries: [], viaUpdater: false };
      panels.push(panel);
    }
    panel.entries.push(entry);
  }
  return panels
    .filter((panel) => panel.entries.length > 0)
    .map((panel) => ({ ...panel, viaUpdater: panel.entries.every((entry) => entry.appliesVia === 'updater') }));
}

// ---- Values in words ----

/** On or Off as the row's words read it (deviceDisabled true is "Gyro runs on the PC: Off"). */
export function shownOn(words: SwitchWords, stored: boolean): boolean {
  return words.inverted ? !stored : stored;
}

/** The stored value for On or Off as the row's words read them. */
export function storedFor(words: SwitchWords, on: boolean): boolean {
  return words.inverted ? !on : on;
}

/** A choice's words: the mockup's, else the value with a capital ("Canary"). */
export function choiceWords(words: SwitchWords, value: string): ChoiceWords {
  const known = words.choices?.[value];
  if (known) return known;
  const capital = value.length > 0 ? `${value[0]!.toUpperCase()}${value.slice(1)}` : value;
  return { option: capital, chip: capital };
}

/** The choices a drop-down or control offers: the drawn ones (else the catalog's), and the values in use outside them. */
export function offeredChoices(entry: CatalogEntry, words: SwitchWords, inUse: ReadonlyArray<SwitchValue | undefined> = []): string[] {
  const all = entry.choices ?? [];
  const drawn = words.offered ? words.offered.filter((value) => all.includes(value)) : [...all];
  const extra = inUse.filter((value): value is string => typeof value === 'string' && !drawn.includes(value));
  return [...drawn, ...all.filter((value) => extra.includes(value) && !drawn.includes(value))];
}

/** "1 name", "3 names". */
export function namesText(n: number): string {
  return `${n.toLocaleString('en-US')} name${n === 1 ? '' : 's'}`;
}

/** A value as a row or "was" line reads it: "On", "Off", "Not set", "Standard actions only", "3 names". */
export function valueText(entry: CatalogEntry, value: SwitchValue | undefined): string {
  if (value === undefined) return 'Not set';
  const words = wordsFor(entry);
  if (typeof value === 'boolean') return shownOn(words, value) ? 'On' : 'Off';
  if (Array.isArray(value)) return value.length === 0 ? 'no names' : namesText(value.length);
  return choiceWords(words, value).option;
}

/** A value as a chip reads it: "On", "Off", "Up to sensitive", "Canary", "none", "revu, github +1". */
export function chipValue(entry: CatalogEntry, value: SwitchValue | undefined): string {
  if (value === undefined) return 'not reported';
  const words = wordsFor(entry);
  if (typeof value === 'boolean') return shownOn(words, value) ? 'On' : 'Off';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'none';
    const shown = value.slice(0, 2).join(', ');
    return value.length > 2 ? `${shown} +${(value.length - 2).toLocaleString('en-US')}` : shown;
  }
  return choiceWords(words, value).chip;
}

/** Whether two values of one switch mean the same: lists as sets of names, letter case aside (as the service and PC do). */
export function sameValue(a: SwitchValue | undefined, b: SwitchValue | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    const left = new Set(a.map((name) => name.toLowerCase()));
    const right = new Set(b.map((name) => name.toLowerCase()));
    return left.size === right.size && [...left].every((name) => right.has(name));
  }
  return a === b;
}

// ---- Unsaved edits ----

/** The person's edits over the saved policy, kept as edits so a Refresh can lay them over what another admin saved. */
export interface PolicyEdits {
  /** For everyone: key -> the new value, or null for "Not set". */
  fleet: Record<string, SwitchValue | null>;
  /** Exceptions: object id -> key -> the new value, or null to take that exception away. */
  people: Record<string, Record<string, SwitchValue | null>>;
  /** The sign-in e-mail chosen in the picker for a person added here (shown until the service names them). */
  upns: Record<string, string>;
}

export const NO_EDITS: PolicyEdits = Object.freeze({ fleet: {}, people: {}, upns: {} }) as PolicyEdits;

/** The policy with the edits over it. */
export interface PolicyDraft {
  fleet: Switches;
  people: Record<string, PolicyPerson>;
}

function copyValue(value: SwitchValue): SwitchValue {
  return Array.isArray(value) ? [...value] : value;
}

function applyLevel(base: Switches, edits: Record<string, SwitchValue | null> | undefined): Switches {
  const out: Switches = {};
  for (const [key, value] of Object.entries(base)) out[key] = copyValue(value);
  for (const [key, value] of Object.entries(edits ?? {})) {
    if (value === null) delete out[key];
    else out[key] = copyValue(value);
  }
  return out;
}

export function draftOf(saved: Pick<DesktopPolicyView, 'fleet' | 'people'>, edits: PolicyEdits): PolicyDraft {
  const people: Record<string, PolicyPerson> = {};
  const oids = new Set([...Object.keys(saved.people), ...Object.keys(edits.people)]);
  for (const oid of oids) {
    const was = saved.people[oid];
    const switches = applyLevel(was?.switches ?? {}, edits.people[oid]);
    if (Object.keys(switches).length === 0) continue;
    const upn = was?.upn ?? edits.upns[oid];
    people[oid] = { switches, ...(was?.name ? { name: was.name } : {}), ...(upn ? { upn } : {}) };
  }
  return { fleet: applyLevel(saved.fleet, edits.fleet), people };
}

/** One unsaved change: a key for everyone, or a key of one person's exceptions. */
export interface PolicyChange {
  oid: string | null;
  key: string;
}

/** The unsaved changes: edits whose value differs from the saved one (an edit back to the saved value is not a change). */
export function changesOf(saved: Pick<DesktopPolicyView, 'fleet' | 'people'>, edits: PolicyEdits): PolicyChange[] {
  const changes: PolicyChange[] = [];
  for (const [key, value] of Object.entries(edits.fleet)) {
    if (!sameValue(saved.fleet[key], value ?? undefined)) changes.push({ oid: null, key });
  }
  for (const [oid, keys] of Object.entries(edits.people)) {
    for (const [key, value] of Object.entries(keys)) {
      if (!sameValue(saved.people[oid]?.switches[key], value ?? undefined)) changes.push({ oid, key });
    }
  }
  return changes;
}

/** Whether a key for everyone is unsaved. */
export function fleetChanged(saved: Pick<DesktopPolicyView, 'fleet'>, edits: PolicyEdits, key: string): boolean {
  return Object.hasOwn(edits.fleet, key) && !sameValue(saved.fleet[key], edits.fleet[key] ?? undefined);
}

/** Whether a person's exception for a key is unsaved. */
export function personChanged(saved: Pick<DesktopPolicyView, 'people'>, edits: PolicyEdits, oid: string, key: string): boolean {
  const keys = edits.people[oid];
  return keys !== undefined && Object.hasOwn(keys, key) && !sameValue(saved.people[oid]?.switches[key], keys[key] ?? undefined);
}

export function withFleetEdit(edits: PolicyEdits, key: string, value: SwitchValue | null): PolicyEdits {
  return { ...edits, fleet: { ...edits.fleet, [key]: value } };
}

export function withPersonEdit(edits: PolicyEdits, oid: string, key: string, value: SwitchValue | null, upn?: string): PolicyEdits {
  return {
    ...edits,
    people: { ...edits.people, [oid]: { ...(edits.people[oid] ?? {}), [key]: value } },
    upns: upn ? { ...edits.upns, [oid]: upn } : edits.upns,
  };
}

/** Takes every exception of one person away. */
export function withoutPerson(edits: PolicyEdits, draft: PolicyDraft, oid: string): PolicyEdits {
  const keys = Object.keys(draft.people[oid]?.switches ?? {});
  const cleared: Record<string, SwitchValue | null> = { ...(edits.people[oid] ?? {}) };
  for (const key of keys) cleared[key] = null;
  return { ...edits, people: { ...edits.people, [oid]: cleared } };
}

/** What Save sends: the whole policy as the person left it, and the revision it was loaded at. */
export function saveBody(saved: Pick<DesktopPolicyView, 'fleet' | 'people' | 'revision'>, edits: PolicyEdits): DesktopPolicyBody {
  const draft = draftOf(saved, edits);
  const people: Record<string, { switches: Switches }> = {};
  for (const [oid, person] of Object.entries(draft.people)) people[oid] = { switches: person.switches };
  return { fleet: draft.fleet, people, expectedRevision: saved.revision };
}

/** "2 unsaved changes". */
export function unsavedText(n: number): string {
  return `${n.toLocaleString('en-US')} unsaved change${n === 1 ? '' : 's'}`;
}

/** The save bar's confirmation. */
export function savedText(revision: number): string {
  return `Saved as revision ${revision.toLocaleString('en-US')}. PCs follow within minutes.`;
}

/** A save refused because another admin saved first (409 policy_revision_conflict); the person's edits are kept. */
export const CONFLICT_TEXT = 'Someone else saved a change; Refresh to see it. Your changes are kept.';

// ---- People ----

const OID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** How a person reads: their name when the service knows one, else the sign-in e-mail, else "Unknown person". */
export function personTitle(person: { name?: string; upn?: string } | null | undefined): string {
  return person?.name?.trim() || person?.upn?.trim() || 'Unknown person';
}

/** The line under a person's title: the e-mail under a name, else nothing. */
export function personSub(person: { name?: string; upn?: string } | null | undefined): string | null {
  return person?.name?.trim() && person.upn?.trim() ? person.upn.trim() : null;
}

/** GET /v1/admin/desktop/policy/resolve?upn=: the person behind a sign-in e-mail, from desktop enrollment. */
export interface ResolvedPerson {
  oid: string;
  upn: string;
}

/** The service's answer when no PC enrolled with that address (404 person_not_enrolled): nothing is added. */
export const NOT_ENROLLED_TEXT = "This person doesn't have Gyro on a PC yet.";

/**
 * The object id of someone already on the policy (saved, or added here and not saved yet), by their sign-in e-mail,
 * letter case aside; null otherwise, and the screen asks the service (GET /v1/admin/desktop/policy/resolve).
 */
export function oidForEmail(email: string, people: Record<string, PolicyPerson>): string | null {
  const wanted = email.trim().toLowerCase();
  if (!wanted) return null;
  for (const [oid, person] of Object.entries(people)) {
    if (person.upn?.toLowerCase() === wanted && OID_RE.test(oid.toLowerCase())) return oid.toLowerCase();
  }
  return null;
}

/** What a failed lookup of a person says: no PC yet, an ambiguous address, a role that may not, or try again. */
export function resolveFailureText(error: unknown, email: string): string {
  const status = (error as { status?: unknown } | null)?.status;
  const message = error instanceof Error ? error.message.trim() : '';
  if (status === 404) return NOT_ENROLLED_TEXT;
  if (status === 409) return message && message !== 'person_ambiguous' ? message : `More than one person signed in to Gyro on a PC with ${email}, so it cannot say which one is meant.`;
  if (status === 401) return 'Your sign-in has expired. Sign in again to add an exception.';
  if (status === 403) return 'Only IT admins can add exceptions.';
  return `Gyro could not look up ${email} just now. Try again in a minute.`;
}

/** People with exceptions, as the list shows them: by name or e-mail, people without either last. */
export function peopleRows(draft: PolicyDraft): Array<{ oid: string; person: PolicyPerson }> {
  return Object.entries(draft.people)
    .map(([oid, person]) => ({ oid, person }))
    .sort((a, b) => {
      const left = a.person.name?.trim() || a.person.upn?.trim() || '';
      const right = b.person.name?.trim() || b.person.upn?.trim() || '';
      if (!left !== !right) return left ? -1 : 1;
      return left.localeCompare(right, 'en-US', { sensitivity: 'base' }) || a.oid.localeCompare(b.oid);
    });
}

/** "5 people get some switches differently from everyone". */
export function peopleText(n: number): string {
  return n === 1 ? '1 person gets some switches differently from everyone' : `${n.toLocaleString('en-US')} people get some switches differently from everyone`;
}

/** "1 person", "3 people". */
export function personCount(n: number): string {
  return n === 1 ? '1 person' : `${n.toLocaleString('en-US')} people`;
}

/** The line under a row of For everyone: who gets it differently ("Off for 1 person", "Different for 2 people"). */
export interface ExceptionSummaryPart {
  /** The words before the link: "Off for", "Different for". */
  lead: string;
  count: number;
}

export function exceptionSummary(entry: CatalogEntry, draft: PolicyDraft): ExceptionSummaryPart[] {
  const values = Object.values(draft.people)
    .map((person) => person.switches[entry.key])
    .filter((value): value is SwitchValue => value !== undefined);
  if (values.length === 0) return [];
  const words = wordsFor(entry);
  if (words.summary === 'different' || entry.type === 'list') return [{ lead: 'Different for', count: values.length }];
  const order = entry.type === 'boolean' ? ['On', 'Off'] : (entry.choices ?? []).map((choice) => choiceWords(words, choice).chip);
  const counts = new Map<string, number>();
  for (const value of values) {
    const text = entry.type === 'boolean' ? (shownOn(words, value as boolean) ? 'On' : 'Off') : chipValue(entry, value);
    counts.set(text, (counts.get(text) ?? 0) + 1);
  }
  const known = order.filter((text) => counts.has(text));
  const rest = [...counts.keys()].filter((text) => !known.includes(text));
  return [...known, ...rest].map((text) => ({ lead: `${text} for`, count: counts.get(text)! }));
}

/** The note under "They get": what everyone else gets for that switch. */
export function everyoneElseText(entry: CatalogEntry, fleet: Switches): string {
  const value = fleet[entry.key];
  if (value === undefined) return "Everyone else keeps their PC's IT setting";
  if (entry.type === 'list') return `Everyone else gets ${Array.isArray(value) && value.length === 0 ? 'an empty list' : `${namesText((value as string[]).length)}`}`;
  return `Everyone else gets ${entry.type === 'choice' ? chipValue(entry, value) : valueText(entry, value)}`;
}

/**
 * The value "They get" starts at for a switch: the opposite of everyone's on/off (stored true when everyone's is not set:
 * a feature on for one person, or Gyro off for one person), else the first other choice.
 */
export function exceptionDefault(entry: CatalogEntry, fleet: Switches): SwitchValue {
  const value = fleet[entry.key];
  if (entry.type === 'boolean') return typeof value === 'boolean' ? !value : true;
  if (entry.type === 'list') return [];
  const choices = offeredChoices(entry, wordsFor(entry));
  return choices.find((choice) => choice !== value) ?? choices[0] ?? '';
}

// ---- The allow list ----

const CONTROL_RE = /[\u0000-\u001f\u007f]/;

/** Why a name cannot go on the allow list, or null when it can (the service's rules, desktopPolicyCatalog.ts readList). */
export function allowListRefusal(list: readonly string[], typed: string, limits: { entries: number; chars: number } = { entries: 200, chars: 200 }): string | null {
  const name = typed.trim();
  if (!name) return 'Type the connection\'s name first.';
  if (CONTROL_RE.test(name)) return 'A name cannot hold a line break or another control character.';
  if (name.length > limits.chars) return `A name can be at most ${limits.chars.toLocaleString('en-US')} characters.`;
  if (list.some((entry) => entry.toLowerCase() === name.toLowerCase())) return `${name} is already on the list.`;
  if (list.length >= limits.entries) return `The list can hold at most ${limits.entries.toLocaleString('en-US')} names.`;
  return null;
}

// ---- The PCs' reports ----

/** A report older than this is a PC that is not reporting (off, asleep, or gone). */
export const STALE_AFTER_MS = 24 * 60 * 60_000;
/** Rows per page of the PCs table. */
export const PCS_PAGE_SIZE = 10;

/** "Just now", "4 min ago", "2 h ago", "6 days ago". */
export function agoText(at: string | number, now: number): string {
  const ms = typeof at === 'number' ? at : Date.parse(at);
  if (!Number.isFinite(ms)) return '—';
  const minutes = Math.floor(Math.max(0, now - ms) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days.toLocaleString('en-US')} days ago`;
}

/** Whether a PC's last report is too old to say what it runs now. */
export function isStale(row: Pick<DevicePolicyRow, 'reportedAt'>, now: number): boolean {
  const ms = Date.parse(row.reportedAt);
  return Number.isFinite(ms) && now - ms > STALE_AFTER_MS;
}

/** A PC that has applied no revision of the server's switches while there is one: it follows only its IT setting. */
export function followsOnlyItSetting(row: Pick<DevicePolicyRow, 'revision'>, revision: number): boolean {
  return row.revision === 0 && revision > 0;
}

/** The line under a PC's last report: "Revision 43", "Revision 42 · catching up", "Revision 41 · not reporting". */
export function revisionText(row: Pick<DevicePolicyRow, 'revision' | 'reportedAt'>, revision: number, now: number): string {
  if (row.revision === 0 && revision > 0) return isStale(row, now) ? 'No revision yet · not reporting' : 'No revision yet';
  const base = `Revision ${row.revision.toLocaleString('en-US')}`;
  if (isStale(row, now)) return `${base} · not reporting`;
  return row.revision < revision ? `${base} · catching up` : base;
}

/** The PC's name as the table shows it: the Windows computer name, else the device id cut short. */
export function pcName(row: Pick<DevicePolicyRow, 'computerName' | 'deviceId'>): { text: string; isName: boolean } {
  const name = row.computerName?.trim();
  if (name) return { text: name, isName: true };
  return { text: row.deviceId.length > 8 ? `${row.deviceId.slice(0, 8)}…` : row.deviceId, isName: false };
}

/** PCs that differ from everyone come first; each group keeps the service's order (the newest report first). */
export function orderedPcs(rows: readonly DevicePolicyRow[]): DevicePolicyRow[] {
  const differ = rows.filter((row) => row.differsFromFleet.length > 0);
  const match = rows.filter((row) => row.differsFromFleet.length === 0);
  return [...differ, ...match];
}

/** The PCs matching the filter box: by person, PC (computer name or device id) or Gyro version. */
export function filterPcs(rows: readonly DevicePolicyRow[], query: string): DevicePolicyRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...rows];
  return rows.filter((row) => [row.person?.name, row.person?.upn, row.computerName, row.deviceId, row.agentVersion]
    .some((field) => typeof field === 'string' && field.toLowerCase().includes(q)));
}

/** One page of rows (pages from 1) and the "Showing 1–10 of 48 PCs" line. */
export function pageOf<T>(rows: readonly T[], page: number, size = PCS_PAGE_SIZE): { rows: T[]; page: number; pages: number; text: string } {
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const current = Math.min(Math.max(1, Math.floor(page) || 1), pages);
  const start = (current - 1) * size;
  const slice = rows.slice(start, start + size);
  const total = rows.length.toLocaleString('en-US');
  const text = rows.length === 0 ? 'No PCs' : `Showing ${(start + 1).toLocaleString('en-US')}–${(start + slice.length).toLocaleString('en-US')} of ${total} PC${rows.length === 1 ? '' : 's'}`;
  return { rows: slice, page: current, pages, text };
}

/** "What each PC reports it is running with · 44 of 48 run the latest revision (43) · PCs that differ come first". */
export function pcsDescription(rows: readonly DevicePolicyRow[], revision: number): string {
  const latest = rows.filter((row) => row.revision === revision).length;
  return `What each PC reports it is running with · ${latest.toLocaleString('en-US')} of ${rows.length.toLocaleString('en-US')} run the latest revision (${revision.toLocaleString('en-US')}) · PCs that differ come first`;
}

/** A chip of "Differs from everyone": the switch, the PC's value and where it came from. */
export interface DifferenceChip {
  key: string;
  label: string;
  value: string;
  /** Where the value came from; null when the PC did not report the switch. */
  source: SwitchSource | null;
  /** The tooltip: why it differs. */
  title: string;
}

export function differenceChips(row: DevicePolicyRow, view: Pick<DesktopPolicyView, 'revision' | 'fleet' | 'people'>, catalog: readonly CatalogEntry[]): DifferenceChip[] {
  const order = new Map(catalog.map((entry, index) => [entry.key, index]));
  const keys = [...new Set(row.differsFromFleet)].sort((a, b) => (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER));
  const who = personTitle(row.person);
  const exceptions = row.person ? view.people[row.person.oid.toLowerCase()]?.switches ?? view.people[row.person.oid]?.switches : undefined;
  return keys.map((key) => {
    const entry = catalog.find((candidate) => candidate.key === key) ?? { key, label: key, group: '', type: 'boolean' as const, description: '', appliesVia: 'agent' as const };
    const words = wordsFor(entry);
    const reported = Object.hasOwn(row.effective, key);
    const source = reported ? row.sources[key] ?? null : null;
    let title: string;
    if (!reported) title = 'This PC did not report this switch';
    else if (source === 'server') {
      title = exceptions && Object.hasOwn(exceptions, key) ? `Server: an exception for ${who}`
        : row.revision < view.revision ? `Server: the value of revision ${row.revision.toLocaleString('en-US')}`
        : 'Server: set here';
    } else if (source === 'machine') {
      title = row.revision > 0 && row.revision < view.revision ? `IT setting: revision ${row.revision.toLocaleString('en-US')} left this to each PC` : 'IT setting on this PC';
    } else if (source === 'default') title = 'Default: built into Gyro';
    else if (source === 'quarantine') title = 'Stopped on this PC by a remote kill or a revoked key';
    else if (source === 'user-pause') title = 'The person paused Gyro on this PC';
    else title = 'Where this value came from is not known';
    return { key, label: words.short, value: chipValue(entry, reported ? row.effective[key] : undefined), source, title };
  });
}

// ---- The save bar's revision line ----

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function clock(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * "Revision 42 · saved today 16:58 by Taylor Brooks" (the signed-in admin's own save, by their name), "… by
 * zlongo@exante360.com" (another admin's, by the e-mail the service records), "Revision 0 · nothing saved yet".
 */
export function revisionLine(
  view: Pick<DesktopPolicyView, 'revision' | 'updatedAt' | 'updatedBy'>,
  now: number,
  me?: { email: string; name: string },
): string {
  const base = `Revision ${view.revision.toLocaleString('en-US')}`;
  const at = view.updatedAt ? new Date(view.updatedAt) : null;
  if (!at || Number.isNaN(at.getTime())) return view.revision === 0 ? `${base} · nothing saved yet` : base;
  const today = new Date(now);
  const yesterday = new Date(now - 24 * 60 * 60_000);
  const when = sameDay(at, today) ? `today ${clock(at)}`
    : sameDay(at, yesterday) ? `yesterday ${clock(at)}`
    : `${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(at)}, ${clock(at)}`;
  const mine = Boolean(me?.name.trim() && view.updatedBy && view.updatedBy.trim().toLowerCase() === me.email.trim().toLowerCase());
  const by = mine ? me!.name.trim() : view.updatedBy;
  return `${base} · saved ${when}${by ? ` by ${by}` : ''}`;
}

// ---- Tabs and the service's answers ----

/** The page's views, each its own `?view=`, as Support's are. */
export type PcSwitchesView = 'everyone' | 'exceptions' | 'pcs';
export const PC_SWITCHES_VIEWS: readonly PcSwitchesView[] = ['everyone', 'exceptions', 'pcs'];

export function viewFrom(search: string): PcSwitchesView {
  const asked = new URLSearchParams(search).get('view');
  return (PC_SWITCHES_VIEWS as readonly string[]).includes(asked ?? '') ? asked as PcSwitchesView : 'everyone';
}

/** Whether the rail offers PC switches after the probe (GET /v1/admin/desktop/policy): 404 (switched off), 401 and 403 hide it. */
export function pcSwitchesVisible(probeError: unknown): boolean {
  if (probeError === null || probeError === undefined) return true;
  const status = (probeError as { status?: unknown }).status;
  return !(status === 404 || status === 401 || status === 403);
}

/** The rail item's state: `checking` until the probe answers (a deep link waits instead of flashing "not on"). */
export function pcSwitchesNav(consoleUser: boolean, probe: boolean | null): 'checking' | 'shown' | 'hidden' {
  if (!consoleUser) return 'hidden';
  if (probe === null) return 'checking';
  return probe ? 'shown' : 'hidden';
}

/** What a failed save says. A conflict keeps the edits; a refusal naming a switch says which, in the screen's words. */
export function saveFailureText(error: unknown, catalog: readonly CatalogEntry[], people: Record<string, PolicyPerson>): string {
  const status = (error as { status?: unknown } | null)?.status;
  const body = (error as { body?: Record<string, unknown> } | null)?.body;
  if (status === 409) return CONFLICT_TEXT;
  if (status === 401) return 'Your sign-in has expired. Sign in again, then save. Your changes are kept.';
  if (status === 403) return 'Only IT admins can change the PC switches.';
  if (status === 400) {
    const field = typeof body?.field === 'string' ? body.field : '';
    const message = typeof body?.message === 'string' ? body.message : '';
    const parts = field.split('.');
    const key = parts[0] === 'fleet' ? parts[1] : parts[0] === 'people' ? parts[3] : undefined;
    const entry = key ? catalog.find((candidate) => candidate.key === key) : undefined;
    const reason = message.startsWith(field) ? message.slice(field.length).trim() : message;
    if (entry) {
      const whose = parts[0] === 'people' ? `for ${personTitle(people[parts[1] ?? ''])}` : 'for everyone';
      return `Nothing was saved. ${wordsFor(entry).label} ${whose}: ${reason || 'the service refused this value'}.`.replace(/\.\.$/, '.');
    }
    return `Nothing was saved. The service refused the change${message ? `: ${message}` : ''}.`.replace(/\.\.$/, '.');
  }
  const message = error instanceof Error && error.message.trim() ? error.message.trim() : '';
  if (typeof status === 'number' && message && !/^request failed/.test(message)) return message;
  return 'The change could not be saved just now. Your changes are kept; try again in a minute.';
}
